import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';

import { StatsAiStateService } from '../../features/statistics-ai/controller/stats-ai-state.service';
import { TeamDetailHeaderComponent } from '../../features/statistics/view/team-detail-header/team-detail-header.component';
import { StatsAiVideoSelectorComponent } from '../../features/statistics-ai/view/stats-ai-video-selector/stats-ai-video-selector.component';
import { StatsAiDashboardComponent } from '../../features/statistics-ai/view/stats-ai-dashboard/stats-ai-dashboard.component';
import { StatsAiFilterPanelComponent } from '../../features/statistics-ai/view/stats-ai-filter-panel/stats-ai-filter-panel.component';
import { StatsAiDashboardComparisonComponent } from '../../features/statistics-ai/view/stats-ai-dashboard-comparison/stats-ai-dashboard-comparison.component';

@Component({
  selector: 'app-stats-ai-team-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    TeamDetailHeaderComponent,
    StatsAiVideoSelectorComponent,
    StatsAiDashboardComponent,
    StatsAiFilterPanelComponent,
    StatsAiDashboardComparisonComponent
  ],
  templateUrl: './stats-ai-team-page.component.html',
  styleUrls: ['./stats-ai-team-page.component.scss'],
  providers: [StatsAiStateService] // Scope local to this page instance
})
export class StatsAiTeamPageComponent implements OnInit, OnDestroy {
  public state = inject(StatsAiStateService);
  private route = inject(ActivatedRoute);

  get isLoading() {
    return this.state.isLoading();
  }

  get teamInfo() {
    return this.state.teamInfo();
  }

  get seasons() {
    return this.state.seasons();
  }

  get currentSeasonId() {
    return this.state.currentSeasonId();
  }

  get opponentStats() {
    return this.state.opponentStatsOverview();
  }

  get opponentTeamName() {
    return this.state.opponentTeamName() || 'Rival';
  }

  get teamName() {
    return this.teamInfo?.name || 'Equipo analizado';
  }

  get showOpponentDashboard(): boolean {
    return this.state.effectiveVideoIds().length === 1 && !!this.opponentStats;
  }

  ngOnInit(): void {
    const teamId = this.route.snapshot.paramMap.get('id');
    if (teamId) {
      this.state.loadInitialData(teamId);
    } else {
      console.error("No team ID found in route for Stats AI");
    }
  }

  ngOnDestroy(): void {
    this.state.clearState();
  }

  onSeasonChange(event: MatSelectChange): void {
    const seasonId = event.value as number | null;
    const teamId = this.state.currentTeamId();
    if (teamId) {
      // Upon season change, we load the videos for that season on page 1
      this.state.loadTeamVideos(teamId, seasonId, 1);
      // We also clear the selection and stats as the videos changed
      this.state.clearSelection();
    }
  }

  onPageChanged(page: number): void {
    const teamId = this.state.currentTeamId();
    const seasonId = this.state.currentSeasonId();
    if (teamId) {
      this.state.loadTeamVideos(teamId, seasonId, page);
    }
  }

  onAnalyzeClicked(): void {
    // Navigate to Nivel 3 or simply scroll down if dashboard is in the same page
    this.state.loadStats();
    // A timeout to allow DOM to render before scrolling
    setTimeout(() => {
      document.getElementById('stats-ai-dashboard-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }
}
